// components/asset-referral/InvestigationForm.tsx
import React, { useState } from "react";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";

interface InvestigationFormProps {
  initialSummary?: string;
  onSubmit: (summary: string, files: File[]) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  showCancelButton?: boolean;
}

export default function InvestigationForm({
  initialSummary = "",
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitButtonText = "Submit",
  showCancelButton = false,
}: InvestigationFormProps) {
  const [summary, setSummary] = useState(initialSummary);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(summary, selectedFiles);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="bg-white shadow-md rounded-lg p-6 mt-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center">
            <div className="icon-bg">
              <svg
                width="21"
                height="19"
                viewBox="0 0 21 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 15.5C11 12.19 13.69 9.5 17 9.5C18.1 9.5 19.12 9.8 20 10.31V2.5C20 1.96957 19.7893 1.46086 19.4142 1.08579C19.0391 0.710714 18.5304 0.5 18 0.5H2C0.89 0.5 0 1.39 0 2.5V14.5C0 15.0304 0.210714 15.5391 0.585786 15.9142C0.960859 16.2893 1.46957 16.5 2 16.5H11.09C11.04 16.17 11 15.84 11 15.5ZM2 4.5V2.5L10 7.5L18 2.5V4.5L10 9.5L2 4.5ZM18 18.5V16.5H14V14.5H18V12.5L21 15.5L18 18.5Z"
                  fill="#212121"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="referral-form-title">
                {initialSummary
                  ? "Edit Investigation Information"
                  : "Investigation Information"}
              </p>
              <p className="referral-form-description">
                {initialSummary
                  ? "Update the investigation details below."
                  : "Please fill in the investigation details below."}
              </p>
            </div>
          </div>
          {showCancelButton && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>

        <div className="space-y-4">
          <div className="flex flex-col">
            <label className="mb-2 referral-form-label">
              Investigation Summary
            </label>
            <textarea
              className="referral-form-field min-h-[120px] resize-vertical"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Enter detailed investigation summary..."
              required
            />
          </div>

          <MultipleFileUploadComponent
            uploadedFiles={selectedFiles}
            onFilesChange={setSelectedFiles}
          />
        </div>

        <div className="flex justify-end mt-6 gap-4">
          <button
            type="submit"
            className="referral-form-button text-white disabled:opacity-50"
            disabled={isSubmitting || !summary}
          >
            {isSubmitting ? "Submitting..." : submitButtonText}
          </button>
        </div>
      </div>
    </form>
  );
}
